import React, { useState, useEffect, useCallback } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { ContentTitle, LinkTypography, RightGrid } from '../ui/styled';
import BackIcon from '../common/Back/BackIcon';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PageHeading from '../common/Heading/PageHeading';
import CommonSearch from '../common/Search/CommonSearch';
import { LeftGrid } from '../Episode/Episode.Styled';
import { Auth } from 'aws-amplify';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import {
  ORGANIZATION_ADDRESS_AND_CONTACTS_FORM_UPDATE_URL,
  ORGANIZATION_COMMENTS_FORM_UPDATE_URL,
  ORGANIZATION_COMPLIANCE_UPDATE_URL,
  ORGANIZATION_FORM_UPDATE_URL,
  ORGANIZATION_INSTITUTION_FORM_URL,
  ORGANIZATION_LIST_URL,
  ORGANIZATION_REGION_FORM_UPDATE_URL,
  ORGANIZATION_SAP_FORM_URL,
  paginationSize,
} from '../../constants';
import { commonQueryParams } from '../../services/QueryParams/QueryParams';
import { getOrganizationList } from './services/organizationsApi';
import { renderCellExpand } from '../common/GridCell/GridCellExpand';
import {
  getOrganizationForm,
  resetOrganizationForm,
  setNewOrganizationForm,
} from '../../redux/organization/organizationForm/organizationFormSlice';
import {
  getAddressAndContactsForm,
  resetAddressAndContactsForm,
} from '../../redux/organization/organizationTabs/addressAndContacts/addressAndContactsSlice';
import {
  getOrganizationComplianceForm,
  resetOrganizationComplianceForm,
} from '../../redux/organization/organizationTabs/organizationCompliance/organizationComplianceSlice';
import {
  getOrganizationSapForm,
  resetOrganizationSapForm,
} from '../../redux/organization/organizationTabs/organizationSap/organizationSapForm/organizationSapSlice';
import {
  getOrganizationInstitutionForm,
  resetOrganizationInstitutionForm,
} from '../../redux/organization/organizationTabs/organizationInstitution/organizationInstitutionSlice';
import {
  getOrganizationCommentsForm,
  resetOrganizationCommentsForm,
} from '../../redux/organization/organizationTabs/organizationComments/organizationCommentsSlice';
import {
  getOrganizationRegionForm,
  resetOrganizationRegionForm,
} from '../../redux/organization/organizationTabs/organizationRegion/organizationRegionSlice';

const OrganizationList = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [searchOrganizationList, setSearchOrganizationList] = useState('');
  const [organizationList, setOrganizationList] = useState({
    isLoading: false,
    data: [],
    total: 0,
    page: 1,
    pageSize: 10,
    sortModel: '',
  });

  const organizationReferenceData = useSelector(
    (state) => state.organizationReferenceData
  );
  const { countyList, sapInstitutionList } = organizationReferenceData;

  useEffect(() => {
    if (user?.signInUserSession.accessToken.jwtToken) {
      fetchData();
    }
    getUser().then((userData) => setUser(userData));
  }, [
    user?.signInUserSession.accessToken.jwtToken,
    organizationList.pageSize,
    organizationList.page,
    organizationList.sortModel,
    searchOrganizationList,
  ]);

  const getUser = () => {
    return Auth.currentAuthenticatedUser()
      .then((userData) => userData)
      .catch(() => console.log('Not signed in'));
  };

  const QuickSearchToolbar = () => {
    return (
      <CommonSearch
        searchText={searchOrganizationList}
        setSearchText={setSearchOrganizationList}
      />
    );
  };

  const getCountyName = (countyId) => {
    const getCounty = countyList.find((item) => item.id === countyId);
    return getCounty?.value;
  };

  const getSapInstitutionName = (sapInstitutionId) => {
    const getSapInstitution = sapInstitutionList.find(
      (item) => item.id === sapInstitutionId
    );
    return getSapInstitution?.value;
  };

  const fetchData = async () => {
    setOrganizationList((prveState) => ({ ...prveState, isLoading: true }));
    const commonParams = commonQueryParams(
      ORGANIZATION_LIST_URL,
      searchOrganizationList,
      organizationList
    );
    const authorization = `${user?.signInUserSession.accessToken.jwtToken}`;
    const organizations = await getOrganizationList({
      commonParams,
      authorization,
      getCountyName,
      getSapInstitutionName,
    });
    setOrganizationList((prveState) => ({
      ...prveState,
      ...organizations,
    }));
  };

  const handleSortModelChange = useCallback((sortModel) => {
    // Here you save the data you need from the sort model
    setOrganizationList((prveState) => ({
      ...prveState,
      sortModel: [...sortModel],
    }));
  }, []);

  const updateOrganizationAddressAndContacts = (params, authorization) => {
    const commonParams = `${ORGANIZATION_ADDRESS_AND_CONTACTS_FORM_UPDATE_URL}/${params.row.orgId}`;
    dispatch(getAddressAndContactsForm({ commonParams, authorization }));
  };

  const updateOrganizationCompliance = (params, authorization) => {
    const commonParams = `${ORGANIZATION_COMPLIANCE_UPDATE_URL}/${params.row.orgId}`;
    dispatch(getOrganizationComplianceForm({ commonParams, authorization }));
  };

  const updateOrganizationSap = (params, authorization) => {
    const commonParams = `${ORGANIZATION_SAP_FORM_URL}/${params.row.orgId}`;
    dispatch(getOrganizationSapForm({ commonParams, authorization }));
  };

  const updateOrganizationInstitution = (params, authorization) => {
    const commonParams = `${ORGANIZATION_INSTITUTION_FORM_URL}/${params.row.orgId}`;
    dispatch(getOrganizationInstitutionForm({ commonParams, authorization }));
  };

  const updateOrganizationRegion = (params, authorization) => {
    const commonParams = `${ORGANIZATION_REGION_FORM_UPDATE_URL}/${params.row.orgId}`;
    dispatch(getOrganizationRegionForm({ commonParams, authorization }));
  };

  const updateOrganizationComments = (params, authorization) => {
    const commonParams = `${ORGANIZATION_COMMENTS_FORM_UPDATE_URL}/${params.row.orgId}`;
    dispatch(getOrganizationCommentsForm({ commonParams, authorization }));
  };

  const updateOrganizationDetail = (params) => {
    const commonParams = `${ORGANIZATION_FORM_UPDATE_URL}/${params.row.orgId}`;
    const authorization = `${user?.signInUserSession.accessToken.jwtToken}`;
    dispatch(getOrganizationForm({ commonParams, authorization }));
    updateOrganizationAddressAndContacts(params, authorization);
    updateOrganizationCompliance(params, authorization);
    updateOrganizationSap(params, authorization);
    updateOrganizationInstitution(params, authorization);
    updateOrganizationRegion(params, authorization);
    updateOrganizationComments(params, authorization);
    navigate(`/organizations/details/${params.row.orgId}`);
  };

  const columnsData = [
    {
      field: 'orgId',
      headerName: 'Org Id',
      minWidth: 80,
      flex: 1,
      renderCell: (params) => {
        return (
          <LinkTypography
            variant='h6'
            onClick={() => updateOrganizationDetail(params)}
          >
            {params.row.orgId}
          </LinkTypography>
        );
      },
    },
    {
      field: 'orgKey',
      headerName: 'Organization Key',
      minWidth: 280,
      renderCell: renderCellExpand,
      flex: 1,
    },
    {
      field: 'city',
      headerName: 'City',
      renderCell: renderCellExpand,
      minWidth: 130,
      flex: 1,
    },
    {
      field: 'countyId',
      headerName: 'County',
      renderCell: renderCellExpand,
      minWidth: 130,
      flex: 1,
    },
    {
      field: 'isInstitution',
      headerName: 'Is Institution',
      type: 'boolean',
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'institutionCode',
      headerName: 'Institution Code',
      renderCell: renderCellExpand,
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'institutionNumber',
      headerName: 'Institution Number',
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'isSap',
      headerName: 'Is Sap',
      type: 'boolean',
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'sapCode',
      headerName: 'Sap Code',
      minWidth: 100,
      renderCell: renderCellExpand,
      flex: 1,
    },
    {
      field: 'sapNumber',
      headerName: 'Sap Number',
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'sapInstitution',
      headerName: 'Sap Institution',
      minWidth: 130,
      renderCell: renderCellExpand,
      flex: 1,
    },
    {
      field: 'stop',
      headerName: 'Stop',
      type: 'boolean',
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'cbp',
      headerName: 'CBP',
      type: 'boolean',
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'parole',
      headerName: 'Parole',
      type: 'boolean',
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'other',
      headerName: 'Other',
      type: 'boolean',
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'westCare',
      headerName: 'WestCare',
      type: 'boolean',
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'inactive',
      headerName: 'Inactive',
      type: 'boolean',
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'vendor',
      headerName: 'Vendor',
      type: 'boolean',
      minWidth: 100,
      flex: 1,
    },
  ];

  const handelNewOrganizationForm = () => {
    dispatch(resetOrganizationForm());
    dispatch(resetAddressAndContactsForm());
    dispatch(resetOrganizationComplianceForm());
    dispatch(resetOrganizationSapForm());
    dispatch(resetOrganizationInstitutionForm());
    dispatch(resetOrganizationRegionForm());
    dispatch(resetOrganizationCommentsForm());
    dispatch(setNewOrganizationForm(true));
    navigate(`/organizations/details/0`);
  };

  return (
    <>
      <ContentTitle container>
        <RightGrid item xs={12} sm={4}>
          <BackIcon />
          <PageHeading title='Organization List' />
        </RightGrid>
        <Grid item xs={12} sm={4}>
          <Box style={{ alignItems: 'center' }}>
            <Typography variant='h4' align='center'>Organizations</Typography>
          </Box>
        </Grid>        
        <LeftGrid item xs={12} sm={4}>
          <Button
            variant='contained'
            onClick={() => handelNewOrganizationForm()}
          >
            New Organization
          </Button>
        </LeftGrid>
      </ContentTitle>
      <div style={{ height: 670, width: '100%' }}>
        <div style={{ display: 'flex', height: '100%' }}>
          <div style={{ flexGrow: 1 }}>
            <DataGrid
              //autoHeight
              //getRowHeight={() => 'auto'}
              columns={columnsData}
              rows={organizationList.data}
              pageSize={organizationList.pageSize}
              page={organizationList.page - 1}
              rowCount={organizationList.total}
              paginationMode='server'
              sortingMode='server'
              onPageSizeChange={(newPageSize) =>
                setOrganizationList((old) => ({ ...old, pageSize: newPageSize }))
              }
              onPageChange={(newPage) => {
                setOrganizationList((old) => ({ ...old, page: newPage + 1 }));
              }}
              rowsPerPageOptions={paginationSize}
              pagination
              components={{ Toolbar: QuickSearchToolbar }}
              experimentalFeatures={{ newEditingApi: true }}
              onSortModelChange={handleSortModelChange}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
              }
              loading={organizationList.isLoading}
            />
          </div>
        </div>
      </div>      
    </>
  );
};
export default OrganizationList;
